import React, {
    useCallback, useContext, useEffect, useState, useRef
} from 'react';
import {
    get, set, isEmpty, filter, sortBy, cloneDeep, find, map
} from 'lodash';
import config from 'app-config';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { withRouter } from 'react-router-dom';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { WizardPage, wizardProps } from 'e1p-portals-wizard-react';
import { FeatureUtil } from 'e1p-portals-util-js';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useLandingPageUtil, useModifierUtil } from 'e1p-capability-hooks';
import { AmfamOktaTokenContext } from 'e1p-capability-gateway-react';
import metadata from '../CoveragePage.metadata.json5';
import messages from '../CoveragePage.messages';

const LOB = 'personalAuto_EA';

function ViewCoveragePage(props) {
    const translator = useTranslator();
    const {
        wizardData: policyChangeVM,
        updateWizardData,
        authHeader,
        steps,
        jumpTo
    } = props;
    const { opCo } = useContext(AmfamOktaTokenContext);
    const [columnData, setColumnData] = useState({});
    const paperLessIndValue = get(policyChangeVM, 'lobData.personalAuto_EA.paperlessInd.value') === undefined
        ? false : get(policyChangeVM, 'lobData.personalAuto_EA.paperlessInd.value');
    const [checkScrolling, setCheckScrolling] = useState(false);
    const policyState = get(policyChangeVM, 'baseData.policyAddress.state.value.code');
    const isCompOnlyFeatureAvailable = FeatureUtil.isCompOnlyFeatureAvailableForState(policyState);
    const isSurchargeSectionVisible = FeatureUtil.isSurchargeSectionAvailableForState(policyState);
    const isTravelPackageVisible = FeatureUtil.isTravelPackageVisibleForState(policyState);
    const {
        onValidate,
    } = useValidation('ViewEACoveragePage');

    const navRef = useRef();

    const {
        getLandingPageIndexForQuotedJob
    } = useLandingPageUtil();

    const {
        structuredModifiers
    } = useModifierUtil(policyChangeVM);
    const modifiers = structuredModifiers;


    const generateColumnData = () => {
        const lobOfferingPath = 'lobData.personalAuto_EA.offerings';
        const lobOfferings = get(policyChangeVM, `${lobOfferingPath}.value`);
        let filteredOfferings = cloneDeep(lobOfferings);

        filteredOfferings = filteredOfferings.filter((offering) => offering.branchCode !== 'CUSTOM');

        const colData = filteredOfferings
            .map((lobOffering, lobIndex) => {
                // Added this condition to hide the vehicleCoverages from UI for which hidden
                // indicator is coming as true.
                const vehicleData = get(lobOffering, 'coverages.vehicleCoverages', []);

                vehicleData.forEach((coverage, index) => {
                    const filteredCoverages = coverage.coverages
                        .filter((cov) => !(cov.isHiddenInVersion));

                    set(lobOffering, `coverages.vehicleCoverages[${index}].coverages`, filteredCoverages);
                });

                return {
                    name: lobOffering.branchName,
                    code: lobOffering.branchCode,
                    quote: {
                        path: '',
                        data: {}
                    },
                    lob: {
                        path: `${lobOfferingPath}.children[${lobIndex}]`,
                        data: lobOffering
                    },
                    policyViewVM: policyChangeVM
                };
            });

        return sortBy(colData, ['code']);
    };

    useEffect(() => {
        const colData = generateColumnData();

        setColumnData(colData);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const getTotalPremium = useCallback(() => {
        const payPlans = get(columnData[0], 'lob.data.paymentPlans');
         const selectedPlan = filter(payPlans, (plan) => plan.isSelected);
        const totalPremium = selectedPlan[0]?.total?.amount;

        if (!totalPremium && totalPremium !== 0) {
            return undefined;
        }

        return { currency: 'usd', amount: totalPremium };
    }, [columnData]);

    const onNext = useCallback(
       async () => policyChangeVM,
        [policyChangeVM]
    );
    const generateOverrides = useCallback(
        () => {
            const vehicles = get(policyChangeVM, 'lobData.personalAuto_EA.coverables.vehicles.value');
            const premiumTypeLabel = get(policyChangeVM, 'quoteType_Ext.value.code') === 'Quick'
                ? 'Unverified Premium' : 'Verified Premium';
            const overrides = {};

            if (!isEmpty(vehicles)) {
                vehicles.forEach((vehicle, i) => {
                    overrides[`vehicleCoverage${i}`] = {
                        loadingClause: () => { }
                    };
                });
            }

            overrides[premiumTypeLabel] = {
                content: premiumTypeLabel
            };

            const lobOfferingPath = 'lobData.personalAuto_EA.offerings';
            const lobOfferings = get(policyChangeVM, `${lobOfferingPath}.value`);

            overrides.gridContainerLineCoverage = {
                lobOfferings,
                onChangeSubmissionAndSync: () => { },
                onChangeSubmission: () => { },
                onSyncCoverages: () => { },
                onValidate,
                policyState,
                lobIndex: 0,
                quoteIndex: 0,
                viewOnlyMode: true
            };
            overrides.gridContainerVehicleCoverage = {
                lobOfferings,
                onChangeSubmissionAndSync: () => { },
                onChangeSubmission: () => { },
                onSyncCoverages: () => { },
                onValidate,
                policyState,
                lobIndex: 0,
                quoteIndex: 0,
                vehicles: get(
                    policyChangeVM,
                    'lobData.personalAuto_EA.coverables.vehicles.value'
                ),
                quoteDataOfferings: get(policyChangeVM, 'quoteData.offeredQuotes.value'),
                isCompOnlyVisible: isCompOnlyFeatureAvailable,
                onCompOnlyChange: () => { },
                policyTransactionVM: policyChangeVM,
                updateWizardData,
                onCompOnlyChangeForCopyCoverages: () => { },
                columnData,
                viewOnlyMode: true
            };

            return overrides;
        },
        [columnData, isCompOnlyFeatureAvailable, onValidate, policyChangeVM, policyState, updateWizardData]
    );

    const getCovPath = (ID, index) => {
        let covPath = '';

        if (ID === 'Vehicle') {
            covPath = `lobData.personalAuto_EA.offerings.children[0].coverages.vehicleCoverages.children[${index}].coverages`;
        } else if (ID === 'Line') {
            covPath = 'lobData.personalAuto_EA.offerings.children[0].coverages.lineCoverages';
        } else {
            throw new Error('You did not pass in the required ID parameter');
        }

 return covPath;
    };
    // Returns policy line coverage endorsement bundle radio options (Split / Combined Single Limit)
    const getAvailablePolicyLineCoverageBundles = () => {
        const lineCoverages = get(policyChangeVM, getCovPath('Line'), {});

       return map(filter(lineCoverages.value, (cov) => cov.coverageCategoryCode === 'EA_EndorsementPackage', []), (cov) => ({
                id: cov.codeIdentifier,
                displayName: cov.name
            }));
    };

    // Returns selected policy line coverage endorsement bundle
    const getSelectedPolicyLineCoverageBundle = () => {
        const lineCoverages = get(policyChangeVM, getCovPath('Line'), {});

        return get(find(lineCoverages.value, (cov) => cov.coverageCategoryCode === 'EA_EndorsementPackage' && cov.selected), 'codeIdentifier');
    };

    let vehicleDiscountSubtotal = 0;
    let discountSubtotal = 0;
    const lineDiscountSubtotal = get(policyChangeVM, 'lobData.personalAuto_EA.offerings.value[0].premiumSummary', [])
        .find((prem) => prem.premiumSummaryType === 'TotalDiscounts')?.amount || 0;

    get(policyChangeVM, 'lobData.personalAuto_EA.offerings.value[0].coverages.vehicleCoverages').forEach((vehicleCoverage) => {
        vehicleDiscountSubtotal += get(vehicleCoverage, 'premiumSummary', [])
            .find((prem) => prem.premiumSummaryType === 'TotalVehicleDiscounts')?.amount || 0;
    })
    discountSubtotal = vehicleDiscountSubtotal + lineDiscountSubtotal;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        '@field': {
            labelPosition: 'top',
            readOnly: true
        },
        coveragesPageLoadingIndicator: {
            loaded: true,
            text: translator(messages.loadingNextPageMessage)
        },
        paperlessDiscountContainer: {
            visible: get(config, ['operatingCompanyConfig', opCo, 'paperlessOptionEnabled'])
        },
        PaperlessDiscountToggle: {
            required: true,
            value: get(policyChangeVM, 'lobData.personalAuto_EA.paperlessInd.value')
        },
        paperlessEmailId: {
            visible: paperLessIndValue,
            required: paperLessIndValue,
            value: policyChangeVM.lobData.personalAuto_EA.paperlessEmail.value
        },
        coveragesPageContainer: {
            visible: true
        },
        AutoPayDiscountToggle: {
            value: get(policyChangeVM, 'lobData.personalAuto_EA.autoPayDiscInd.value')
        },
        policyTermComponent: {
            transactionVM: policyChangeVM,
            authHeader,
            updateWizardData,
            viewOnly: true
        },
        paymentOptionsID: {
            submissionVM: policyChangeVM,
            authHeader,
            updateWizardData,
            LOB: 'personalAuto_EA',
            viewOnly: true,
            isQuoteStale: false
        },
        monthlyPaymentScheduleComponent: {
            visible: false
        },
        eaSurchargeListComponentId: {
            value: modifiers.filter(
                (item) => item.applied && item.modifierType === "surcharge"
            ),
            visible: (!!filter(modifiers, { applied: true, modifierType: 'fee' }).length
                || !!filter(modifiers, { applied: true, modifierType: 'surcharge' }).length
            ) && isSurchargeSectionVisible,
            transactionVM: policyChangeVM,
            lob: 'PersonalAuto_EA'
        },
        eaDiscountsListComponentId: {
            visible: !!filter(modifiers, { applied: true, modifierType: 'discount' }).length,
            value: modifiers,
            discountsTotal: discountSubtotal !== 0 ? discountSubtotal : undefined
        },
        supportingPolicyComponentContainer: {
            transactionVM: policyChangeVM,
            LOB: 'personalAuto_EA',
            updateWizardData,
            viewOnly: true
        },
        scrollingComponentId: {
            navRef,
            checkScrolling,
            setCheckScrolling,
            scrollableDiv: document.getElementById('gridContainerVehicleCoverageContainer'),
            scrollWidth: 320
        },
        policyLevelCoverageBundle: {
            availableValues: getAvailablePolicyLineCoverageBundles(),
            value: getSelectedPolicyLineCoverageBundle(),
            onValueChange: () => { },
            // Only make it visible if options are available for policy level endorsement packages
            visible: getAvailablePolicyLineCoverageBundles().length > 0
        },
        whatsIncludedLink: {
            disabled: true
        },
        compOnlyLink: {
            disabled: true
        },
        ADDWarningDiv: {
            visible: false
        },
        physicalDamageCovRemovalMsgDiv: {
            visible: false
        },
        travelPackageGrid: {
            visible: isTravelPackageVisible
        },
        buyNowButton: {
            visible: false
        },
        recalculateButton: {
            visible: false
        },
        LiabilityCovCededMsgDiv: {
            visible: false
        },
        paperlessEmailChangedMessageDiv: {
            visible: false
        },
        tncGapMinimumPDLimitMsgDiv: {
            visible: false
        },
        totalPremiumID: {
            value: (() => getTotalPremium())()
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onChangeClause: () => { },
            onSyncCoverages: () => { },
            onChangeSubmissionAndSync: () => { },
            onValidate
        },
    };

    const readValue = useCallback((id, path) => readViewModelValue(metadata.pageContent, policyChangeVM, id, path, overrideProps), [overrideProps, policyChangeVM]);
    const showCustom = true;
    const onCustom = useCallback(
        async () => {
            let newLandingPageIndex = -1;
            const validationErrors = get(policyChangeVM, 'errorsAndWarnings.validationIssues.fieldIssues', []);

            // Need to stay on the page if field issues
            if (validationErrors.length === 0) {
                newLandingPageIndex = getLandingPageIndexForQuotedJob(
                    LOB,
                    steps
                );
            }

            if (newLandingPageIndex >= 0) {
                jumpTo(newLandingPageIndex, true);
            }

            return false;
        },
        [getLandingPageIndexForQuotedJob, jumpTo, policyChangeVM, steps]
    );

    const dataForComponent = {
        columnData,
        modifiers,
        policyChangeVM
    };

    return (
        <WizardPage
            onNext={onNext}
            showCustom={showCustom}
            onCustom={onCustom}
            shouldLink
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={dataForComponent}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

ViewCoveragePage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.shape({})
    }).isRequired,
    ...wizardProps
};
export default withRouter(withAuthenticationContext(ViewCoveragePage));
